import { Icon } from "@iconify/react";
import { Box, Grid, Stack, TableBody, Table as MuiTable, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import moment from "moment";
import React, { useState } from "react";
import AppDialog from "../../components/Common/AppDialog";
import CustomButton from "../../components/Form/CustomButton";
import Helpers from "../../helpers/helpers";

const money = (value) =>
  `$${Number(value || 0).toLocaleString("en-SG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const QuotationDetailsDialog = ({ open, close, quotation, onDelete, onEdit }) => {
  const [expand, setExpand] = useState(false)
  if (!quotation) return null;
  const pricing = quotation.pricing || {};
  const totalInterest = Math.max(
    0,
    Number(pricing.grand_total || 0) - Number(pricing.loan_amount || 0),
  );
  const detailRow = (label, value, strong = false) => (
    <Stack direction="row" justifyContent="space-between" gap={2}>
      <Typography fontSize={14} color={strong ? "#1D1B1B" : "#808588"} fontWeight={strong ? 600 : 400}>
        {label}
      </Typography>
      <Typography fontSize={14} textAlign="right" color={strong ? "#1D1B1B" : "#808588"} fontWeight={strong ? 600 : 400}>
        {value ?? "-"}
      </Typography>
    </Stack>
  );

  return (
    <AppDialog
      open={open}
      close={(e) => { close(); setExpand(false) }}
      title="Quotation Details"
      width="760px"
      fullWidth
      isMobile={window.innerWidth <= 768}
      hideCloseIcon={false}
      contentSx={{ pb: 1 }}
      customTitle={
        <Stack direction="row" justifyContent="space-between" width="100%">
          <CustomButton
            title={<><Icon icon="solar:trash-bin-trash-linear" style={{ marginRight: 6 }} />Delete</>}
            press={onDelete}
            backgroundColor="#FFFFFF"
            textColor="#ED1A3D"
            borderStyle="1px solid #ED1A3D"
            height={34}
            paddingInline={15}
          />
          <CustomButton
            title={<><Icon icon="ri:pencil-line" style={{ marginRight: 6 }} />Edit</>}
            press={() => {
              onEdit();
            }}
            height={34}
            paddingInline={15}
          />
        </Stack>
      }
    >
      <Typography fontSize={16} fontWeight={600}>{quotation.customer_name}</Typography>
      <Typography fontSize={14} color="#808588" mt={0.75} mb={2.5}>
        {quotation.customer_phone} &nbsp; • &nbsp; Created by {quotation.creator?.name || "-"} &nbsp; • &nbsp; {quotation.created_at ? moment(quotation.created_at, "YYYY-MM-DD HH:mm:ss").format("DD MMM YYYY, hh:mm A") : "-"}
      </Typography>

      <Grid container bgcolor="#F5F5F5" borderRadius="10px" py={2} mb={3}>
        {[
          [money(pricing.total_price), "Selling Price", "#ED1A3D"],
          [money(pricing.loan_amount), "Loan Amount"],
          [`${pricing.loan_term || ""} mths`, `${Number(pricing.interest_rate || 0)?.toFixed(2)}% P.A.`],
          [money(pricing.monthly_instalment), "Est Monthly Instalment"],
        ].map(([value, label, color]) => (
          <Grid item xs={6} md={3} textAlign="center" py={0.5} key={label}>
            <Typography fontSize={24} fontWeight={600} color={color || "#1D1B1B"}>{value}</Typography>
            <Typography fontSize={14} color="#808588" mt={0.5}>{label}</Typography>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Typography fontSize={14} fontWeight={600} mb={2}>Vehicle</Typography>
          <Stack spacing={2}>
            {detailRow("Type", quotation?.bike_advert_type ? Helpers.capitalize(quotation.bike_advert_type) : '-')}
            {detailRow("Make & model", `${quotation.make || ""} ${quotation.model || ""}`)}
            {detailRow("Year", quotation.manu_year)}
            {detailRow("Registration", quotation.register_number)}
            {detailRow("Source", quotation.vehicle_source ? quotation.vehicle_source.charAt(0).toUpperCase() + quotation.vehicle_source.slice(1) : "-")}

          </Stack>
          <Typography fontSize={14} fontWeight={600} mt={4} mb={2}>Remarks</Typography>
          <Typography fontSize={14} color="#808588" lineHeight={1.5}>{quotation.remarks || "-"}</Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography fontSize={14} fontWeight={600} mb={2}>Pricing Breakdown</Typography>
          <Stack spacing={2}>
            {detailRow("Machine price", money(pricing.machine_price))}
            {detailRow("Overtrade", money(pricing.overtrade))}
            {detailRow("COE", money(pricing.coe))}
            {detailRow("Insurance", money(pricing.insurance))}
            {detailRow("Admin fee", money(pricing.admin_fee))}
            {detailRow("Other charges", money(pricing.other_charges))}
            {/* {detailRow("Trade In", money(pricing.trade_in))} */}
            {detailRow("Total Selling Price", money(pricing.total_price), true)}
            {detailRow("Downpayment", money(pricing.downpayment))}
            {detailRow("Loan amount", money(pricing.loan_amount))}
            {detailRow("Total interest", money(pricing.interest_amount || totalInterest))}
            {detailRow("Monthly instalment", <Box component="span" color="#ED1A3D">{money(pricing.monthly_instalment)}</Box>, true)}
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Typography fontSize={14} fontWeight={600} mb={2}>Instalment Table
            {/* <span style={{cursor: "pointer" }} onClick={(e) => setExpand(!expand)}  className="text-muted clickable-link mx-3 font-size-12">{expand ? "Hide" : "View"}
              <Icon style={{ fontSize: "20px"}} icon={expand ? "ri-arrow-drop-up-fill" : "ri-arrow-drop-down-fill"} />
            </span> */}
            <CustomButton
              title={expand ? <><Icon icon="ri-eye-off-line" style={{ marginRight: "5px" }} />Hide</> : <><Icon icon="ri-eye-line" style={{ marginRight: "5px" }} />View</>}
              press={() => setExpand(!expand)}
              backgroundColor="transparent"
              textColor="black"
              borderStyle="1px solid black"
              marginLeft="10px"
              height={15}
              padding={"12px"}
            />
          </Typography>


          {expand && <TableContainer sx={{ mb: 1, overflowX: "auto" }}>
            <MuiTable size="small" aria-label="Full instalment preview">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#F5F5F5" }}>
                  {[
                    "Year",
                    "Interest ($)",
                    "Monthly Instalment ($)",
                    "Last Instalment ($)",
                  ].map((heading, index) => (
                    <TableCell
                      key={heading}
                      align={index === 0 ? "left" : "right"}
                      sx={{ color: "#747B80", fontSize: 12, py: 1.2 }}
                    >
                      {heading}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>

                {quotation?.pricing?.instalment_schedule?.length > 0 ?
                  quotation?.pricing?.instalment_schedule?.map((row) => (
                    <TableRow
                      key={row.year}
                      sx={{
                        backgroundColor:
                          row.year === Math.round((pricing.loan_term || 0) / 12)
                            ? "#F6F0E4"
                            : "transparent",
                      }}
                    >
                      <TableCell sx={{ fontSize: 13 }}>{row.year}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 13 }}>
                        {Number(row.interest || 0).toLocaleString("en-SG", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 13 }}>
                        {Number(row.monthly_instalment || 0).toLocaleString("en-SG", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 13 }}>
                        {Number(row.last_instalment || 0).toLocaleString("en-SG", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                    </TableRow>
                  )) :
                  <div>No data available</div>}
              </TableBody>
            </MuiTable>
          </TableContainer>}
        </Grid>
      </Grid>
    </AppDialog>
  );
};

export default QuotationDetailsDialog;
